import * as THREE from 'three';

import { EngineClass } from '../../systems/ClassRegistry.js';
import { property } from '../../utils/serialization/decorator.js';

import { CSGComponent } from './CSGComponent.js';

import type { CSGComponentOptions } from './CSGComponent.js';
import type { ActiveManifold, ManifoldWrapper } from '../../csg/ManifoldWrapper.js';


export interface CSGCubeComponentOptions extends CSGComponentOptions {
  /** The dimensions of the cube */
  dimensions?: THREE.Vector3;
  /** Whether the cube should be centered */
  centered?: boolean;
}

/** Cube shape for CSG operations. */
@EngineClass('CSGCubeComponent')
export class CSGCubeComponent extends CSGComponent<CSGCubeComponentOptions> {

  @property({ category: 'CSG', description: 'Dimensions of the cube' })
  protected dimensions: THREE.Vector3 = new THREE.Vector3(1, 1, 1);

  @property({ category: 'CSG', description: 'Whether the cube is centered at origin' })
  protected centered: boolean = true;

  public override initialize(options?: CSGCubeComponentOptions): void {
    super.initialize(options);
    if (options?.dimensions !== undefined) this.dimensions = options.dimensions.clone();
    this.centered = options?.centered ?? this.centered;
  }

  public override postLoad(): void {
    super.postLoad();
  }

  public override createManifold(manifoldWrapper: ManifoldWrapper, isRoot: boolean = false): ActiveManifold | null {
    if (!this.enabled || this._pendingRemoval) return null;

    const scaledDimensions = this.dimensions.clone().multiply(this.scale);
    const manifold = manifoldWrapper.cube(scaledDimensions, this.centered);
    manifold.setMaterial(this.material);

    if (!isRoot) {
      const deg = 180 / Math.PI;
      manifold.rotate(new THREE.Vector3(
        this.rotation.x * deg,
        this.rotation.y * deg,
        this.rotation.z * deg,
      ));
      manifold.translate(this.position);
    }

    return manifold;
  }

  public setDimensions(dimensions: THREE.Vector3): void {
    this.dimensions = dimensions;
  }

  public getDimensions(): THREE.Vector3 {
    return this.dimensions;
  }

  public override getEditorClassIcon(): string | null {
    return 'Icon_Cube';
  }
}
